(() => {
    const instanceStore = window.aocClickToScrollInstances || new Map();
    window.aocClickToScrollInstances = instanceStore;

    class AocClickToScroll {
        constructor(container) {
            this.container = container;
            this.header = document.querySelector('header');
            this.backToTopBtn = this.container.querySelector('.aoc-back-to-top');
            this.outerContainer = this.container.querySelector('.outer-container');
            this.carouselViewport = this.container.querySelector('[data-aoc-embla-viewport]');
            this.carouselMediaQuery = window.matchMedia('(max-width: 1000px)');
            this.embla = null;
            this.emblaRetryTimer = null;
            this.emblaRetryCount = 0;

            this.onClick = this.onClick.bind(this);
            this.onBackToTop = this.onBackToTop.bind(this);
            this.onWindowScroll = this.onWindowScroll.bind(this);
            this.onAnchorHover = this.onAnchorHover.bind(this);
            this.onBreakpointChange = this.onBreakpointChange.bind(this);
            this.updateCarouselState = this.updateCarouselState.bind(this);

            this.container.addEventListener('click', this.onClick);
            this.container.addEventListener('mouseover', this.onAnchorHover);
            this.backToTopBtn?.addEventListener('click', this.onBackToTop);
            this.carouselViewport?.addEventListener('scroll', this.updateCarouselState, {
                passive: true
            });
            window.addEventListener('scroll', this.onWindowScroll, {
                passive: true
            });

            if (this.carouselMediaQuery.addEventListener) {
                this.carouselMediaQuery.addEventListener('change', this.onBreakpointChange);
            } else {
                this.carouselMediaQuery.addListener(this.onBreakpointChange);
            }

            this.backToTopBtn?.classList.add('is-hidden');
            this.onBreakpointChange();
        }

        onBreakpointChange() {
            if (this.carouselMediaQuery.matches) {
                this.initializeCarousel();
            } else {
                this.destroyCarousel();
            }

            window.requestAnimationFrame(this.updateCarouselState);
        }

        initializeCarousel() {
            if (this.embla || !this.carouselViewport) return;

            if (typeof window.EmblaCarousel !== 'function') {
                this.scheduleCarouselRetry();
                return;
            }

            this.clearCarouselRetry();

            this.embla = window.EmblaCarousel(this.carouselViewport, {
                align: 'start',
                containScroll: 'trimSnaps',
                dragFree: true,
                loop: false,
            });

            this.container.classList.add('is-embla-active');
            this.embla.on('select', this.updateCarouselState);
            this.embla.on('scroll', this.updateCarouselState);
            this.embla.on('reInit', this.updateCarouselState);
            this.updateCarouselState();
        }

        scheduleCarouselRetry() {
            const maximumRetries = 100;

            if (this.emblaRetryTimer || this.emblaRetryCount >= maximumRetries) return;

            this.emblaRetryTimer = window.setTimeout(() => {
                this.emblaRetryTimer = null;
                this.emblaRetryCount += 1;

                if (this.carouselMediaQuery.matches) {
                    this.initializeCarousel();
                }
            }, 100);
        }

        clearCarouselRetry() {
            if (this.emblaRetryTimer) {
                window.clearTimeout(this.emblaRetryTimer);
                this.emblaRetryTimer = null;
            }

            this.emblaRetryCount = 0;
        }

        destroyCarousel() {
            this.clearCarouselRetry();

            if (this.embla) {
                this.embla.destroy();
                this.embla = null;
            }

            this.container.classList.remove('is-embla-active');
            this.outerContainer?.classList.remove('is-at-end', 'has-no-overflow');
        }

        updateCarouselState() {
            if (!this.outerContainer || !this.carouselViewport) return;

            let hasOverflow = false;
            let isAtEnd = false;

            if (this.embla) {
                hasOverflow = this.embla.canScrollPrev() || this.embla.canScrollNext();
                isAtEnd = hasOverflow && !this.embla.canScrollNext();
            } else if (this.carouselMediaQuery.matches) {
                const maximumScroll = this.carouselViewport.scrollWidth - this.carouselViewport.clientWidth;
                hasOverflow = maximumScroll > 1;
                isAtEnd = hasOverflow && this.carouselViewport.scrollLeft >= maximumScroll - 2;
            }

            this.outerContainer.classList.toggle('has-no-overflow', !hasOverflow);
            this.outerContainer.classList.toggle('is-at-end', isAtEnd);
        }

        onAnchorHover(event) {
            const anchor = event.target.closest('.anchor');
            if (!anchor || !this.container.contains(anchor)) return;

            const index = anchor.dataset.bannerIndex;

            this.container.querySelectorAll('.banner-item').forEach((item) => {
                item.classList.remove('is-visible');
                item.classList.add('is-hidden');
            });

            const matchingBanner = this.container.querySelector(
                `.banner-item[data-banner-index="${index}"]`
            );

            if (matchingBanner) {
                matchingBanner.classList.remove('is-hidden');
                matchingBanner.classList.add('is-visible');
            }
        }

        onClick(event) {
            const anchor = event.target.closest('.anchor');

            if (!anchor || !this.container.contains(anchor)) return;

            const targetId = anchor.dataset.target;
            const target = document.getElementById(targetId);

            if (!target) return;

            const listSection = target.closest('.shopify-section');
            let scrollTarget = listSection?.previousElementSibling;

            // Skip anything between the list and previous Shopify section.
            while (
                scrollTarget &&
                !scrollTarget.classList.contains('shopify-section')
            ) {
                scrollTarget = scrollTarget.previousElementSibling;
            }

            // Fall back to the list when no previous section exists.
            scrollTarget ||= target;

            const headerHeight =
                this.header?.getBoundingClientRect().height || 0;

            const spacing = 16;

            const top =
                scrollTarget.getBoundingClientRect().top +
                window.scrollY -
                headerHeight -
                spacing;

            window.scrollTo({
                top: Math.max(0, top),
                behavior: 'smooth'
            });
        }

        onBackToTop() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }

        onWindowScroll() {
            if (!this.backToTopBtn) return;

            const threshold = this.container.offsetTop + this.container.offsetHeight;
            this.backToTopBtn.classList.toggle('is-hidden', window.scrollY < threshold);
        }

        destroy() {
            this.destroyCarousel();
            this.container.removeEventListener('click', this.onClick);
            this.container.removeEventListener('mouseover', this.onAnchorHover);
            this.backToTopBtn?.removeEventListener('click', this.onBackToTop);
            this.carouselViewport?.removeEventListener('scroll', this.updateCarouselState);
            window.removeEventListener('scroll', this.onWindowScroll);

            if (this.carouselMediaQuery.removeEventListener) {
                this.carouselMediaQuery.removeEventListener('change', this.onBreakpointChange);
            } else {
                this.carouselMediaQuery.removeListener(this.onBreakpointChange);
            }
        }
    }

    function init(scope = document) {
        scope.querySelectorAll('[data-section-type="aoc-click-to-scroll"]').forEach((container) => {
            const id = container.dataset.sectionId;

            if (instanceStore.has(id)) {
                instanceStore.get(id).destroy();
            }

            instanceStore.set(id, new AocClickToScroll(container));
        });
    }

    if (!window.aocClickToScrollEventsBound) {
        window.aocClickToScrollEventsBound = true;

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => init(), {
                once: true
            });
        } else {
            init();
        }

        if (window.Shopify?.designMode) {
            document.addEventListener('shopify:section:load', (event) => init(event.target));
        }

        document.addEventListener('shopify:section:unload', (event) => {
            const id = event.detail.sectionId;
            const instance = instanceStore.get(id);

            if (instance) {
                instance.destroy();
                instanceStore.delete(id);
            }
        });
    } else {
        init();
    }
})();